<template>
    <Teleport to="body">
        <Transition name="fade">
            <div
                v-if="show"
                class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-800/75 p-0 md:p-4"
                @click.self="$emit('close')"
            >
                <!-- SUCCESS STEP -->
                <div
                    v-if="missingCardStep === 1"
                    class="relative w-full h-full md:h-auto md:max-w-2xl bg-white dark:bg-slate-800 md:rounded-lg shadow-xl overflow-y-auto"
                >
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6">
                        <div class="flex items-center gap-3">
                            <div
                                class="flex w-12 h-12 p-3.5 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="credit-card"
                                    class="w-5 h-5 text-slate-600 dark:text-slate-300"
                                />
                            </div>
                            <h2
                                class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                            >
                                Missing card request
                            </h2>
                        </div>
                        <button
                            type="button"
                            @click="$emit('close')"
                            class="text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors hidden md:block"
                        >
                            <Icon icon="close" class="w-6 h-6" />
                        </button>
                    </div>

                    <!-- Success Content -->
                    <div
                        class="flex-1 flex flex-col items-center justify-center p-6 min-h-[300px]"
                    >
                        <div
                            class="flex w-16 h-16 justify-center items-center rounded-full bg-emerald-50 dark:bg-emerald-500/20 mb-4"
                        >
                            <Icon icon="check-green" class="w-8 h-8" />
                        </div>

                        <div
                            class="flex w-full max-w-[560px] flex-col items-center gap-4"
                        >
                            <h3
                                class="text-slate-800 dark:text-slate-100 text-center text-xl font-medium leading-7"
                            >
                                Card request sent
                            </h3>
                            <p
                                class="text-slate-800 dark:text-slate-100 text-center text-sm font-normal leading-5"
                            >
                                Our team will review and work on adding it as
                                soon as possible. You'll get an email
                                notification once the card is available in the
                                list.
                            </p>
                        </div>
                    </div>

                    <!-- Mobile bottom bar -->
                    <div
                        class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] border-t border-slate-200 dark:border-slate-700"
                    >
                        <button
                            type="button"
                            @click="$emit('close')"
                            class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                            Ok, got it
                        </button>
                    </div>

                    <!-- Desktop footer -->
                    <div class="hidden md:flex justify-end items-center p-6">
                        <button
                            type="button"
                            @click="$emit('close')"
                            class="py-2.5 px-4 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            Ok, got it
                        </button>
                    </div>
                </div>

                <!-- FORM STEP -->
                <div
                    v-else
                    class="relative w-full h-full md:h-auto md:max-w-2xl bg-white dark:bg-slate-800 md:rounded-lg shadow-xl overflow-y-auto"
                >
                    <!-- Header -->
                    <div class="flex items-center justify-between p-6">
                        <div class="flex items-center gap-3">
                            <div
                                class="flex w-12 h-12 p-3.5 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                            >
                                <Icon
                                    icon="credit-card"
                                    class="w-5 h-5 text-slate-600 dark:text-slate-300"
                                />
                            </div>
                            <h2
                                class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                            >
                                Missing card request
                            </h2>
                        </div>
                        <button
                            type="button"
                            @click="$emit('close')"
                            class="text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                        >
                            <Icon icon="close" class="w-6 h-6" />
                        </button>
                    </div>

                    <!-- Content -->
                    <div class="p-6 pb-24 md:pb-6">
                        <p
                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 mb-6"
                        >
                            Please provide accurate card details about your
                            card, so we can add the correct one to our system.
                            We'll notify you once it's available.
                        </p>

                        <!-- Form -->
                        <div class="flex flex-col gap-6">
                            <!-- Issuing Bank -->
                            <div class="flex flex-col items-start">
                                <label
                                    class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5 mb-2"
                                >
                                    Issuing bank
                                </label>
                                <div
                                    class="flex py-2.5 px-3 items-center gap-2 self-stretch rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus-within:border-slate-800 dark:focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-800 dark:focus-within:ring-slate-500"
                                >
                                    <input
                                        type="text"
                                        v-model="missingCardBank"
                                        placeholder="Enter issuing bank"
                                        class="flex-1 text-slate-800 dark:text-slate-100 dark:placeholder-slate-500 text-sm font-normal leading-5 bg-transparent border-none outline-none focus:ring-0 p-0"
                                    />
                                </div>
                            </div>

                            <!-- Card Name -->
                            <div class="flex flex-col items-start">
                                <label
                                    class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5 mb-2"
                                >
                                    Card name
                                </label>
                                <div
                                    class="flex py-2.5 px-3 items-center gap-2 self-stretch rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus-within:border-slate-800 dark:focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-800 dark:focus-within:ring-slate-500"
                                >
                                    <input
                                        type="text"
                                        v-model="missingCardName"
                                        placeholder="Enter card name"
                                        class="flex-1 text-slate-800 dark:text-slate-100 dark:placeholder-slate-500 text-sm font-normal leading-5 bg-transparent border-none outline-none focus:ring-0 p-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile bottom bar -->
                    <div
                        class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] border-t border-slate-200 dark:border-slate-700"
                    >
                        <div class="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                @click="$emit('close')"
                                class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                @click="$emit('submit')"
                                :disabled="
                                    !missingCardBank || !missingCardName
                                "
                                class="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 disabled:bg-slate-400 disabled:cursor-not-allowed"
                            >
                                Send request
                            </button>
                        </div>
                    </div>

                    <!-- Desktop Footer -->
                    <div
                        class="hidden md:flex justify-between items-center p-6"
                    >
                        <button
                            type="button"
                            @click="$emit('close')"
                            class="py-2.5 px-4 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            @click="$emit('submit')"
                            :disabled="!missingCardBank || !missingCardName"
                            class="py-2.5 px-4 rounded bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
                        >
                            Send request
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';

defineProps({
    show: Boolean,
    missingCardStep: Number,
});

const missingCardBank = defineModel('missingCardBank', { type: String, default: '' });
const missingCardName = defineModel('missingCardName', { type: String, default: '' });

defineEmits(['close', 'submit']);
</script>
